import { Gavel, Loader2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { formatGBP } from "@/lib/format";

/**
 * Confirmation modal shown before a bid is submitted.
 * Renders only values passed in — no fee calculations or estimation.
 */
export function BidConfirmDialog({
  open,
  onOpenChange,
  title,
  image,
  bidPence,
  currentBidPence,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  title: string;
  image?: string | null;
  bidPence: number;
  currentBidPence: number | null;
  onConfirm: () => void;
  isPending: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={(o) => !isPending && onOpenChange(o)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Confirm your bid</DialogTitle>
          <DialogDescription>
            Bids on BidNova are binding. Please review before confirming.
          </DialogDescription>
        </DialogHeader>

        <div className="flex gap-3 rounded-xl border border-border/60 bg-surface-2 p-3">
          {image ? (
            <img
              src={image}
              alt=""
              className="h-16 w-16 rounded-lg object-cover border border-border/60"
            />
          ) : (
            <div className="h-16 w-16 rounded-lg bg-surface-2" />
          )}
          <div className="min-w-0">
            <p className="truncate text-sm font-medium">{title}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Current bid: <span className="text-foreground">{formatGBP(currentBidPence ?? 0)}</span>
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-gold/30 bg-gold/5 p-4 text-center">
          <p className="text-[11px] uppercase tracking-widest text-muted-foreground">Your bid</p>
          <p className="mt-1 font-display text-3xl text-gold-gradient">{formatGBP(bidPence)}</p>
        </div>

        <p className="text-[11px] text-muted-foreground">
          Fees, shipping and totals are shown at checkout if you win.
        </p>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button
            className="bg-gold-gradient hover:opacity-90 press-tap"
            onClick={onConfirm}
            disabled={isPending}
          >
            {isPending ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Placing bid…</>
            ) : (
              <><Gavel className="h-4 w-4 mr-2" /> Confirm bid</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
